/**
 * @fileoverview What a piece of advice's button does, where it does not depend
 * on the screen. Adding a plant does, so the screen that has the form passes
 * it in; so does adding a photo, which needs the plant.
 *
 * The advice's own link (open_link) is read the way every link is (safeLink):
 * a page in the app through the router, a web page in a new tab, an email
 * address or a phone number as it is, and nothing unsafe. tipCanRun says
 * whether a button would do anything where it is shown, so a screen can leave
 * out one that would not.
 *
 * Where: TipViews (TopTip), TodayNotice and the plant form (PlantFormDialog,
 * the after-add advice's Add photo).
 * API: none; it navigates (/plants/photos, /subscribe), opens the screen's
 * add form, offers a photo (share/lib/shareMoments offerPhoto), shows Chrome's
 * install dialog, or follows the advice's link.
 * Watch out: open_link goes through shared/lib/safeLink, so a link that is
 * not http(s), mailto, tel or an app path is never followed.
 *
 * @module tips/useTipAction
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useInstallPrompt } from '@/features/welcome/useInstallPrompt';
import { offerPhoto } from '@/features/share/lib/shareMoments';
import { followLink, readLink } from '@/shared/lib/safeLink';
import { useTendingFor } from '@/features/billing/lib/useBilling';
import type { Advice } from './types';

/** What the screen showing advice knows: the plant it is about, and its add form. */
export interface TipContext {
  /** The plant the advice is about, for adding a photo to it. */
  plantObjId?: number;
  /** Opens the screen's form to add a plant. */
  onAddPlant?: () => void;
  /** Whether the browser will offer to install the app now; unknown is taken as yes. */
  canInstall?: boolean;
  /** A member tending someone else's garden (useTendingFor): there is nothing to subscribe to there. */
  tending?: boolean;
}

/**
 * Whether a piece of advice's button would do anything on this screen.
 *
 * A share button is the share panel's own (ShareMoments), adding a plant or a
 * photo needs what the screen passes in, and a link needs to be one that can
 * be followed safely. Subscribe does nothing for a member tending someone
 * else's garden (context.tending (useTendingFor; B23: the invitee only uses the inviter's plan)).
 *
 * @param advice - The advice.
 * @param context - What the screen knows.
 * @returns True when pressing the button does something.
 */
export function tipCanRun(advice: Advice, context: TipContext = {}): boolean {
  switch (advice.button?.does ?? null) {
    case 'add_plant': return Boolean(context.onAddPlant);
    case 'add_photo': return Boolean(context.plantObjId);
    case 'open_photos': return true;
    case 'subscribe': return !context.tending;
    case 'install': return context.canInstall !== false;
    case 'open_link': return readLink(advice.button?.url) !== null;
    default: return false;
  }
}

/**
 * What a piece of advice's button does: add a plant (the screen's form),
 * offer a photo, open Photos, subscribe (never in someone else's garden), install, or follow its own link.
 *
 * @returns Runs advice's button, with what the screen knows (the plant, its add form).
 */
export function useTipAction(): (advice: Advice, context?: TipContext) => void {
  const navigate = useNavigate();
  const { promptInstall } = useInstallPrompt();
  /** Someone else's garden: the owner's plan, so Subscribe does nothing (B23). */
  const tending = useTendingFor() !== null;
  return React.useCallback((advice, context = {}) => {
    switch (advice.button?.does ?? null) {
      case 'add_plant': context.onAddPlant?.(); break;
      case 'add_photo': if (context.plantObjId) offerPhoto(context.plantObjId); break;
      case 'open_photos': navigate('/plants/photos'); break;
      case 'subscribe': if (!tending) navigate('/subscribe'); break;
      case 'install': void promptInstall(); break;
      // The advice's own link (a feedback form, a page in the app); nothing unsafe.
      case 'open_link': {
        const link = readLink(advice.button?.url);
        if (link) followLink(link, navigate);
        break;
      }
      default: break;
    }
  }, [navigate, promptInstall, tending]);
}
